/**
 * Folder-tree grouping for deliverable files whose `file_name`
 * still carries the vendor's relative path (Studio / vendor folder
 * drop). Shared by the operator library, Studio, the Autodesk
 * convert list, the vendor upload page, and VirtualSpace file rails.
 *
 * Not a Dropbox clone: strip the shared wrapper folder, keep the
 * remaining directories collapsible, and show basenames on leaves.
 */

import { archiveDisplayBaseName } from "@/lib/virtualspace/deliverable-archive-naming";
import { fileHasDisplaySortKey } from "@/lib/virtualspace/bim-file-grouping";
import { compareBimCadFileOrder } from "@/lib/virtualspace/bim-file-sort";

export type DeliverableFolderNode<T> = {
  name: string;
  /** Relative path from the stripped wrapper (no trailing slash). */
  path: string;
  fileCount: number;
  folders: DeliverableFolderNode<T>[];
  files: T[];
};

export type DeliverableFolderTree<T> = {
  /** Shared leading directories stripped from every path, or null. */
  sharedPrefix: string | null;
  roots: DeliverableFolderNode<T>[];
  looseFiles: T[];
};

export function splitDeliverableFilePath(
  fileName: string | null | undefined,
): string[] {
  if (!fileName) return [];
  return fileName
    .replace(/\\/g, "/")
    .replace(/^\/+/, "")
    .split("/")
    .map((part) => part.trim())
    .filter((part) => part.length > 0 && part !== "." && part !== "..");
}

export function deliverableFileBaseName(
  fileName: string | null | undefined,
): string {
  const base = archiveDisplayBaseName(fileName);
  return base || fileName?.trim() || "";
}

export function deliverableFileParentPath(
  fileName: string | null | undefined,
): string | null {
  const parts = splitDeliverableFilePath(fileName);
  if (parts.length < 2) return null;
  return parts.slice(0, -1).join("/");
}

/**
 * Leading directory segments shared by every path. Never consumes a
 * file's basename, so a homogeneous `Drawings/A.dwg` + `Drawings/B.dwg`
 * drop strips `Drawings` and leaves two loose files (no tree).
 */
export function commonLeadingDirectoryPrefix(paths: string[]): string[] {
  const split = paths
    .map(splitDeliverableFilePath)
    .filter((parts) => parts.length > 0);
  if (split.length < 2) return [];

  const prefix: string[] = [];
  const max = Math.min(...split.map((parts) => parts.length));
  for (let i = 0; i < max; i++) {
    const segment = split[0]![i];
    if (!segment) break;
    if (!split.every((parts) => parts[i] === segment)) break;
    if (split.some((parts) => i === parts.length - 1)) break;
    prefix.push(segment);
  }
  return prefix;
}

type MutableNode<T> = {
  name: string;
  path: string;
  files: T[];
  children: Map<string, MutableNode<T>>;
};

function emptyNode<T>(name: string, path: string): MutableNode<T> {
  return { name, path, files: [], children: new Map() };
}

function freezeNode<T>(
  node: MutableNode<T>,
  getName: (file: T) => string | null | undefined,
): DeliverableFolderNode<T> {
  const folders = [...node.children.values()]
    .map((child) => freezeNode(child, getName))
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true }),
    );
  const files = [...node.files].sort((a, b) =>
    compareBimCadFileOrder(
      { file_name: getName(a) },
      { file_name: getName(b) },
    ),
  );
  return {
    name: node.name,
    path: node.path,
    fileCount:
      files.length + folders.reduce((sum, folder) => sum + folder.fileCount, 0),
    folders,
    files,
  };
}

export function buildDeliverableFileFolderTree<T>(
  files: readonly T[],
  getName: (file: T) => string | null | undefined,
): DeliverableFolderTree<T> {
  const rawPaths = files.map((file) => getName(file) ?? "");
  const prefixParts = commonLeadingDirectoryPrefix(rawPaths);
  const sharedPrefix =
    prefixParts.length > 0 ? prefixParts.join("/") : null;
  const prefixLen = prefixParts.length;

  const rootMap = new Map<string, MutableNode<T>>();
  const looseFiles: T[] = [];

  for (const file of files) {
    const parts = splitDeliverableFilePath(getName(file));
    const relative = parts.slice(prefixLen);
    if (relative.length === 0) {
      looseFiles.push(file);
      continue;
    }
    if (relative.length === 1) {
      looseFiles.push(file);
      continue;
    }
    const top = relative[0]!;
    let node = rootMap.get(top);
    if (!node) {
      node = emptyNode(top, top);
      rootMap.set(top, node);
    }
    let cur = node;
    for (let i = 1; i < relative.length - 1; i++) {
      const seg = relative[i]!;
      const nextPath = `${cur.path}/${seg}`;
      let child = cur.children.get(seg);
      if (!child) {
        child = emptyNode(seg, nextPath);
        cur.children.set(seg, child);
      }
      cur = child;
    }
    cur.files.push(file);
  }

  const roots = [...rootMap.values()]
    .map((node) => freezeNode(node, getName))
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true }),
    );
  const sortedLoose = [...looseFiles].sort((a, b) =>
    compareBimCadFileOrder(
      { file_name: getName(a) },
      { file_name: getName(b) },
    ),
  );

  return { sharedPrefix, roots, looseFiles: sortedLoose };
}

/**
 * Use the folder tree when at least one directory remains after
 * stripping the shared wrapper and nobody has pinned a manual order.
 */
export function shouldUseFolderTree<T>(
  files: readonly T[],
  getName: (file: T) => string | null | undefined,
  getSortKey?: (file: T) => number | null | undefined,
): boolean {
  if (files.length < 2) return false;
  if (
    getSortKey &&
    files.some((file) => fileHasDisplaySortKey(getSortKey(file)))
  ) {
    return false;
  }
  return buildDeliverableFileFolderTree(files, getName).roots.length > 0;
}
